/**
 * Date formatting utility with organization preference support
 *
 * ## Future Enhancement: Organization Date Format Preferences
 *
 * To add organization-level date format preferences:
 *
 * ### 1. Database (Backend)
 * Add to prisma/schema.prisma:
 * ```prisma
 * model Organization {
 *   // ... existing fields
 *   dateFormat String @default("MM/DD/YYYY") // "MM/DD/YYYY", "DD/MM/YYYY", "YYYY-MM-DD"
 * }
 * ```
 * Run: `npx prisma migrate dev --name add_org_date_format_preference`
 *
 * ### 2. API (Backend)
 * Return dateFormat in organization/campaign responses:
 * - GET /campaigns/:id/organizations/:orgId
 * - GET /auth/me
 *
 * ### 3. Store (Frontend)
 * Add to stores/authStore.ts or campaignStore.ts:
 * ```typescript
 * const userDateFormat = ref<DateFormat>('MM/DD/YYYY');
 *
 * // Set from API response
 * userDateFormat.value = user.organization.dateFormat;
 * ```
 *
 * ### 4. Components (Frontend)
 * Pass preference to formatting functions:
 * ```typescript
 * import { formatDate } from '@shared/utils/dateFormat';
 * import { useAuthStore } from '@/stores/authStore';
 *
 * const authStore = useAuthStore();
 * const formatted = formatDate(dateValue, authStore.userDateFormat);
 * ```
 *
 * ### 5. Admin UI (Frontend)
 * Add to organization settings page:
 * - Dropdown to select: "MM/DD/YYYY", "DD/MM/YYYY", "YYYY-MM-DD"
 * - PUT /admin/organizations/:id endpoint to save preference
 */

import { DATE_FORMAT, DATETIME_FORMAT, TIME_FORMAT } from "../constants/displayFormats.js";

export type DateFormat = "DD/MM/YYYY" | "MM/DD/YYYY" | "YYYY-MM-DD";

// Derived from the SSOT enums in constants/displayFormats.ts. Template-literal
// `${ENUM}` expands to the union of the enum's string VALUES, so this stays a
// string-literal union — existing `someString as DateDisplayFormat` casts and
// `displayFormat?: DateDisplayFormat` fields keep compiling. (The old union also
// included ""; dropped — the consuming fields are optional, so "absent" is
// undefined.)
export type DateDisplayFormat = `${DATE_FORMAT}` | `${DATETIME_FORMAT}` | `${TIME_FORMAT}`;

/**
 * Get the IANA timezone's offset (in milliseconds) from UTC at a given instant.
 * Positive when the zone is east of UTC, negative when west. DST-aware.
 *
 * Internal helper for parseOffsetlessDatetime — exported only so tests can
 * exercise edge cases. Most callers want parseOffsetlessDatetime directly.
 */
export function getTimezoneOffsetMs(timeZone: string, atInstant: Date): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    hour: "2-digit",
    hour12: false,
    minute: "2-digit",
    month: "2-digit",
    second: "2-digit",
    timeZone,
    year: "numeric",
  }).formatToParts(atInstant);
  const get = (t: string): number => parseInt(parts.find((p) => p.type === t)?.value ?? "0", 10);
  // Intl can emit "hour=24" for midnight-rollover on some platforms; that
  // means the local wall-clock is 00:xx on the NEXT day. Normalize the hour
  // to 0 AND advance the day, otherwise the reconstructed instant lands on
  // midnight of the wrong day and the computed offset is 24h off.
  let h = get("hour");
  let day = get("day");
  if (h === 24) {
    h = 0;
    day += 1;
  }
  // Reconstruct the local wall-clock as if it were UTC. Diff vs the real
  // UTC instant is the zone's offset at that moment. Date.UTC handles
  // day overflow correctly (e.g. day=32 → next month).
  const localAsIfUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    day,
    h,
    get("minute"),
    get("second")
  );
  return localAsIfUtc - atInstant.getTime();
}

/**
 * Returns true when `value` names a real IANA timezone the JS engine
 * recognises. We probe via `Intl.DateTimeFormat`, which throws RangeError on
 * unknown zones — far cheaper than maintaining a hand-rolled list.
 */
export function isValidIanaTimezone(value: unknown): value is string {
  if (typeof value !== "string" || value.length === 0) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/**
 * Resolve the IANA zone a CRM config wants us to use when interpreting
 * offsetless datetime strings from that CRM. Reads `defaultTimezone` from
 * the freeform `CrmConfig.config` JSON; falls back to "UTC" when missing,
 * malformed, or not a valid IANA zone — so providers that didn't set it
 * (or set garbage) keep the previous behavior instead of crashing inside
 * `Intl.DateTimeFormat` at sync time.
 */
export function resolveDefaultTimezone(crmMetadata: unknown): string {
  if (crmMetadata && typeof crmMetadata === "object") {
    const tz = (crmMetadata as Record<string, unknown>).defaultTimezone;
    if (isValidIanaTimezone(tz)) return tz;
  }
  return "UTC";
}

/**
 * Parse an offsetless wall-clock datetime string (e.g. "2024-10-17T15:02:13"
 * or "2024-10-17 15:02:13") as wall-clock-in-`timeZone`, and return the UTC
 * instant it represents.
 *
 * Background: some CRMs ship datetime values without an offset marker
 * (no `Z`, no `±HH:MM`). `new Date(string)` in those cases reads the
 * wall-clock in the JS engine's local zone — which is brittle (server TZ
 * matters) and usually wrong (the CRM's wall-clock is NOT the server's
 * wall-clock). This helper makes the zone explicit.
 *
 * @param value - "YYYY-MM-DD HH:MM:SS" or "YYYY-MM-DDTHH:MM:SS"
 *                (fractional seconds tolerated, no offset / Z accepted)
 * @param timeZone - IANA zone name. Defaults to "UTC" so callers that don't
 *                   know the zone get the same behavior as a `Z`-suffixed parse.
 */
export function parseOffsetlessDatetime(
  value: null | string | undefined,
  timeZone = "UTC"
): Date | null {
  if (!value) return null;
  const normalized = value.replace(" ", "T");
  // Match "YYYY-MM-DDTHH:MM:SS" with optional ".fff" fractional seconds.
  // Reject anything with a trailing Z or ±HH:MM — those are self-describing
  // and should not be re-interpreted by a default zone.
  const m = normalized.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?$/);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const d = parseInt(m[3], 10);
  const h = parseInt(m[4], 10);
  const mi = parseInt(m[5], 10);
  const s = parseInt(m[6], 10);

  // Treat wall-clock as UTC for the first estimate, then correct by the
  // zone's offset at that instant. Iterate once so DST transitions resolve
  // stably: the offset at the corrected instant is what we actually want.
  const guess = Date.UTC(y, mo - 1, d, h, mi, s);
  if (Number.isNaN(guess)) return null;

  // Reject inputs whose components don't round-trip. The regex passes
  // syntactically valid but semantically invalid wall-clocks like
  // "2024-02-30T15:02:13" or "2024-13-01T00:00:00"; Date.UTC silently
  // normalizes those into a different real instant (Feb 30 → Mar 2), which
  // would let a malformed CRM payload sync as if it were a valid date.
  // Better to bounce here.
  const probe = new Date(guess);
  if (
    probe.getUTCFullYear() !== y ||
    probe.getUTCMonth() !== mo - 1 ||
    probe.getUTCDate() !== d ||
    probe.getUTCHours() !== h ||
    probe.getUTCMinutes() !== mi ||
    probe.getUTCSeconds() !== s
  ) {
    return null;
  }

  if (timeZone === "UTC") return new Date(guess);

  const offsetAtGuess = getTimezoneOffsetMs(timeZone, new Date(guess));
  const corrected = guess - offsetAtGuess;
  const offsetAtCorrected = getTimezoneOffsetMs(timeZone, new Date(corrected));
  return new Date(guess - offsetAtCorrected);
}

/**
 * Compute age in whole years from a date of birth.
 *
 * DOB is a calendar-day field (no time, no tz), so we read its components with
 * UTC getters — matching how the rest of the calendar-day formatters treat
 * these values. "Today" is genuinely local: it should reflect the viewer's
 * current calendar day, not UTC's. Pre-fix, every site used local getters on
 * the DOB side too, causing the wrong age on the user's birthday for any
 * viewer west of UTC.
 *
 * @returns Whole-years age, or null if the input is missing/invalid.
 */
export function calculateAgeFromDob(dob: Date | null | number | string | undefined): null | number {
  if (!dob) return null;

  const dobDate = new Date(dob);
  if (isNaN(dobDate.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - dobDate.getUTCFullYear();
  const monthDiff = today.getMonth() - dobDate.getUTCMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dobDate.getUTCDate())) {
    age--;
  }
  return age;
}

/**
 * Format a calendar date according to the specified format.
 *
 * Calendar-day fields (dateOpened, dateClosed, dateOfBirth, etc.) are stored
 * tz-free — typically as a UTC-midnight instant or a "YYYY-MM-DD" string — so
 * we read them with UTC getters. Reading with local getters shifts the day for
 * any viewer west of UTC (the classic off-by-one).
 *
 * For real instants with a meaningful wall-clock time, use formatDateTime.
 *
 * @param dateValue - Date string or Date object
 * @param format - Date format preference (defaults to American MM/DD/YYYY)
 * @returns Formatted date string or "N/A" if invalid
 */
export function formatDate(
  dateValue: Date | null | string | undefined,
  format: DateFormat = "MM/DD/YYYY"
): string {
  if (!dateValue) return "N/A";

  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return "N/A";

    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");

    switch (format) {
      case "MM/DD/YYYY":
        return `${month}/${day}/${year}`;
      case "DD/MM/YYYY":
        return `${day}/${month}/${year}`;
      case "YYYY-MM-DD":
        return `${year}-${month}-${day}`;
      default:
        return `${month}/${day}/${year}`; // Default to American
    }
  } catch {
    return String(dateValue);
  }
}

/**
 * Format a date/time with explicit display format (for field-level formatting)
 * @param dateValue - Date string or Date object
 * @param displayFormat - Display format from field configuration
 * @returns Formatted date/time string
 */
export function formatDateWithDisplayFormat(
  dateValue: Date | null | string | undefined,
  displayFormat: string
): string {
  if (!dateValue) return "N/A";

  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return "N/A";

    switch (displayFormat) {
      // Date formats — render in UTC because calendar dates are stored
      // tz-free (typically as a UTC-midnight instant). Local rendering
      // shifts the day for any viewer west of UTC.
      case "DATE_SHORT":
        // Short: 1/12/1964 (no leading zeros)
        return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;

      case "DATE_MEDIUM":
        // Medium: Jan 12, 1964
        return date.toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
          timeZone: "UTC",
          year: "numeric",
        });

      case "DATE_LONG":
        // Long: January 12, 1964
        return date.toLocaleDateString("en-US", {
          day: "numeric",
          month: "long",
          timeZone: "UTC",
          year: "numeric",
        });

      case "DATE_ISO":
        // ISO: 1964-01-12
        return date.toISOString().split("T")[0];

      // Time formats
      case "TIME_12HR":
        // 12-hour: 3:45 PM
        return date.toLocaleTimeString("en-US", {
          hour: "numeric",
          hour12: true,
          minute: "2-digit",
        });

      case "TIME_24HR":
        // 24-hour: 15:45
        return date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          hour12: false,
          minute: "2-digit",
        });

      case "TIME_12HR_SECONDS":
        // 12-hour with seconds: 3:45:30 PM
        return date.toLocaleTimeString("en-US", {
          hour: "numeric",
          hour12: true,
          minute: "2-digit",
          second: "2-digit",
        });

      case "TIME_24HR_SECONDS":
        // 24-hour with seconds: 15:45:30
        return date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          hour12: false,
          minute: "2-digit",
          second: "2-digit",
        });

      // DateTime formats
      case "DATETIME_SHORT":
        // Short: 1/12/1964 3:45 PM
        const dateShort = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
        const timeShort = date.toLocaleTimeString("en-US", {
          hour: "numeric",
          hour12: true,
          minute: "2-digit",
        });
        return `${dateShort} ${timeShort}`;

      case "DATETIME_MEDIUM":
        // Medium: Jan 12, 1964 3:45 PM
        const dateMedium = date.toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
        const timeMedium = date.toLocaleTimeString("en-US", {
          hour: "numeric",
          hour12: true,
          minute: "2-digit",
        });
        return `${dateMedium} ${timeMedium}`;

      case "DATETIME_LONG":
        // Long: January 12, 1964 at 3:45 PM
        const dateLong = date.toLocaleDateString("en-US", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
        const timeLong = date.toLocaleTimeString("en-US", {
          hour: "numeric",
          hour12: true,
          minute: "2-digit",
        });
        return `${dateLong} at ${timeLong}`;

      case "DATETIME_ISO":
        // ISO: 1964-01-12T15:45:30Z
        return date.toISOString();

      default:
        // Default to American short with leading zeros: 01/12/1964
        return formatDate(dateValue, "MM/DD/YYYY");
    }
  } catch {
    return String(dateValue);
  }
}

/**
 * Format a calendar date in long form for hover tooltips. UTC for the same
 * reason as formatDate — calendar dates are stored tz-free.
 *
 * @param dateValue - Date string or Date object
 * @returns Long form date string (e.g., "Monday, January 12, 1964")
 */
export function formatDateLong(dateValue: Date | null | string | undefined): string {
  if (!dateValue) return "";

  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return "";

    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      timeZone: "UTC",
      weekday: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

/**
 * Format a date for display in data tables
 * Uses toLocaleDateString with the appropriate locale
 */
export function formatDateForTable(
  dateValue: Date | null | string | undefined,
  format: DateFormat = "MM/DD/YYYY"
): string {
  if (!dateValue) return "N/A";

  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return "N/A";

    // Use the custom formatter for consistency
    return formatDate(date, format);
  } catch {
    return String(dateValue);
  }
}

/**
 * Get locale string for toLocaleDateString based on format preference
 */
export function getLocaleForFormat(format: DateFormat): string {
  switch (format) {
    case "MM/DD/YYYY":
      return "en-US";
    case "DD/MM/YYYY":
      return "en-GB";
    case "YYYY-MM-DD":
      return "sv-SE"; // Swedish uses YYYY-MM-DD
    default:
      return "en-US";
  }
}

/**
 * Get date format options for toLocaleDateString
 */
export function getDateFormatOptions(_format: DateFormat): Intl.DateTimeFormatOptions {
  return {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  };
}

/**
 * Format a time value for display
 * @param timeValue - Time string (HH:mm:ss or HH:mm) or Date object
 * @returns Formatted time string (e.g., "3:45 PM")
 */
export function formatTime(timeValue: Date | null | string | undefined): string {
  if (!timeValue) return "N/A";

  try {
    // If it's already in HH:mm or HH:mm:ss format
    if (typeof timeValue === "string" && /^\d{1,2}:\d{2}(?::\d{2})?$/.test(timeValue)) {
      const [hours, minutes] = timeValue.split(":").map(Number);
      const period = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12;
      return `${displayHours}:${String(minutes).padStart(2, "0")} ${period}`;
    }

    // Try parsing as a date to extract time
    const date = new Date(timeValue);
    if (!isNaN(date.getTime())) {
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        hour12: true,
        minute: "2-digit",
      });
    }

    return String(timeValue);
  } catch {
    return String(timeValue);
  }
}

/**
 * Format a datetime with both date and time for display.
 *
 * Real instants (createdAt, lastModified, audit timestamps) render in the
 * viewer's local zone — they represent a moment in time and the wall-clock
 * time matters. We deliberately do NOT delegate to formatDate here because
 * formatDate renders in UTC for calendar-day correctness.
 *
 * @param dateValue - Date string or Date object
 * @param format - Date format preference (defaults to American MM/DD/YYYY)
 * @returns Formatted datetime string (e.g., "01/12/1964 3:45 PM")
 */
export function formatDateTime(
  dateValue: Date | null | string | undefined,
  format: DateFormat = "MM/DD/YYYY"
): string {
  if (!dateValue) return "N/A";

  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return "N/A";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    let datePart: string;
    switch (format) {
      case "DD/MM/YYYY":
        datePart = `${day}/${month}/${year}`;
        break;
      case "YYYY-MM-DD":
        datePart = `${year}-${month}-${day}`;
        break;
      case "MM/DD/YYYY":
      default:
        datePart = `${month}/${day}/${year}`;
        break;
    }
    const timePart = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      hour12: true,
      minute: "2-digit",
    });

    return `${datePart} ${timePart}`;
  } catch {
    return String(dateValue);
  }
}

/**
 * Format a datetime in long form for hover tooltips
 * @param dateValue - Date string or Date object
 * @returns Long form datetime string (e.g., "Monday, January 12, 1964 at 3:45 PM")
 */
export function formatDateTimeLong(dateValue: Date | null | string | undefined): string {
  if (!dateValue) return "";

  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return "";

    const datePart = date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      weekday: "long",
      year: "numeric",
    });

    const timePart = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      hour12: true,
      minute: "2-digit",
    });

    return `${datePart} at ${timePart}`;
  } catch {
    return "";
  }
}
